import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuBriefcase, LuCheck, LuCircleHelp, LuFolderKanban, LuTreePalm } from 'react-icons/lu';
import Popover from '../../../components/ui/Popover';
import { useTranslation } from '../../../i18n/LanguageContext';
import { BAR_TONES, PROJECT_SWATCHES } from './calendarTones';

const STATES = ['planned', 'inProgress', 'overdue', 'completed', 'screenFail'];

const triggerClass = (active) => `tw-inline-flex tw-h-8 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-px-3 tw-text-xs tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 ${
  active
    ? 'tw-border-amber-500/30 tw-bg-amber-400/15 tw-text-amber-800 dark:tw-border-amber-400/30 dark:tw-text-amber-200'
    : 'tw-border-slate-200 tw-bg-white/60 tw-text-slate-600 hover:tw-bg-white hover:tw-text-slate-900 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-300 dark:hover:tw-bg-white/[0.08] dark:hover:tw-text-white'
}`;

/** A sample window bar: light window, darker visit day, optional tick. */
const SampleBar = ({ state, done }) => (
  <span aria-hidden="true" className={`tw-relative tw-flex tw-h-4 tw-w-14 tw-shrink-0 tw-overflow-hidden tw-rounded ${BAR_TONES[state].bar}`}>
    <span className={`tw-absolute tw-inset-y-0 tw-left-[40%] tw-w-[20%] ${BAR_TONES[state].mark}`} />
    {done && (
      <span className="tw-absolute tw-inset-y-0 tw-right-[8%] tw-grid tw-place-items-center">
        <span className="tw-grid tw-h-3 tw-w-3 tw-place-items-center tw-rounded-full tw-bg-emerald-500 tw-text-white"><LuCheck className="tw-h-2 tw-w-2" strokeWidth={4} /></span>
      </span>
    )}
  </span>
);

SampleBar.propTypes = { state: PropTypes.string.isRequired, done: PropTypes.bool };

/** "How do I read this?" — what bars, colours and marks mean. */
export const CalendarHelpButton = () => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  return (
    <>
      <button ref={anchorRef} type="button" onClick={() => setOpen(!open)} aria-expanded={open} className={triggerClass(false)}>
        <LuCircleHelp aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        {label('help.button')}
      </button>
      <Popover anchorRef={anchorRef} open={open} onClose={() => setOpen(false)} align="end" label={label('help.title')} className="tw-w-[min(24rem,calc(100vw-1.5rem))] tw-p-4">
        <div className="tw-space-y-3 tw-text-sm">
          <p className="tw-font-semibold tw-text-slate-900 dark:tw-text-white">{label('help.title')}</p>
          <div className="tw-flex tw-items-start tw-gap-3">
            <SampleBar state="planned" />
            <p className="tw-text-xs tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-300">{label('help.bar')}</p>
          </div>
          <div className="tw-flex tw-items-start tw-gap-3">
            <SampleBar state="completed" done />
            <p className="tw-text-xs tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-300">{label('help.done')}</p>
          </div>
          <p className="tw-text-xs tw-leading-relaxed tw-text-slate-600 dark:tw-text-slate-300">{label('help.stripe')}</p>
          <ul className="tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-2 tw-border-t tw-border-slate-200 tw-pt-3 tw-text-xs tw-text-slate-600 dark:tw-border-white/10 dark:tw-text-slate-300">
            {STATES.map((state) => (
              <li key={state} className="tw-flex tw-items-center tw-gap-2">
                <span aria-hidden="true" className={`tw-h-3 tw-w-5 tw-shrink-0 tw-rounded ${BAR_TONES[state].mark}`} />
                {label(`legend.${state}`)}
              </li>
            ))}
            <li className="tw-flex tw-items-center tw-gap-2">
              <span aria-hidden="true" className="tw-grid tw-h-3 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded tw-border tw-border-dashed tw-border-slate-400"><LuBriefcase className="tw-h-2 tw-w-2" /></span>
              {label('legend.job')}
            </li>
            <li className="tw-flex tw-items-center tw-gap-2">
              <span aria-hidden="true" className="tw-grid tw-h-3 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded tw-bg-sky-500/15 tw-text-sky-700 dark:tw-text-sky-300"><LuTreePalm className="tw-h-2 tw-w-2" /></span>
              {label('legend.leave')}
            </li>
          </ul>
          <p className="tw-border-t tw-border-slate-200 tw-pt-3 tw-text-xs tw-leading-relaxed tw-text-slate-500 dark:tw-border-white/10 dark:tw-text-slate-400">{label('help.tips')}</p>
        </div>
      </Popover>
    </>
  );
};

/** Which projects' visits are shown (each with its stripe colour). */
export const ProjectFilterButton = ({ projects, hiddenProjects, onToggleProject, onShowAll }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`profilePage.calendar.${key}`, vars);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  if (projects.length === 0) return null;
  const hiddenCount = projects.filter((project) => hiddenProjects.has(project.id)).length;
  return (
    <>
      <button ref={anchorRef} type="button" onClick={() => setOpen(!open)} aria-expanded={open} className={triggerClass(hiddenCount > 0)}>
        <LuFolderKanban aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        {hiddenCount > 0 ? label('projectFilter.someHidden', { shown: projects.length - hiddenCount, total: projects.length }) : label('projectFilter.button')}
      </button>
      <Popover anchorRef={anchorRef} open={open} onClose={() => setOpen(false)} align="end" label={label('projectFilter.title')} className="tw-w-[min(20rem,calc(100vw-1.5rem))] tw-p-2">
        <p className="tw-px-2 tw-pb-1.5 tw-pt-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-500 dark:tw-text-slate-400">{label('projectFilter.title')}</p>
        <ul className="tw-space-y-0.5">
          {projects.map((project) => {
            const shown = !hiddenProjects.has(project.id);
            return (
              <li key={project.id} className="tw-block">
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={shown}
                  onClick={() => onToggleProject(project.id)}
                  className="tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-rounded-xl tw-bg-transparent tw-px-2.5 tw-py-2 tw-text-left tw-text-sm tw-transition-colors hover:tw-bg-slate-900/[0.05] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:hover:tw-bg-white/[0.07]"
                >
                  <span aria-hidden="true" className={`tw-grid tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-place-items-center tw-rounded-md tw-border ${shown ? 'tw-border-teal-500 tw-bg-teal-500 tw-text-white' : 'tw-border-slate-300 dark:tw-border-white/20'}`}>
                    {shown && <LuCheck className="tw-h-3 tw-w-3" strokeWidth={3} />}
                  </span>
                  <span aria-hidden="true" className={`tw-h-2.5 tw-w-2.5 tw-shrink-0 tw-rounded-full ${PROJECT_SWATCHES[project.slot]}`} />
                  <span className={`tw-min-w-0 tw-flex-1 tw-truncate ${shown ? 'tw-font-medium tw-text-slate-800 dark:tw-text-slate-100' : 'tw-text-slate-400 dark:tw-text-slate-500'}`}>{project.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={onShowAll}
            className="tw-mt-1 tw-w-full tw-rounded-xl tw-bg-transparent tw-px-2.5 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-teal-700 hover:tw-bg-teal-500/10 dark:tw-text-teal-300"
          >
            {label('projectFilter.showAll')}
          </button>
        )}
      </Popover>
    </>
  );
};

ProjectFilterButton.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, slot: PropTypes.number, name: PropTypes.string })).isRequired,
  hiddenProjects: PropTypes.instanceOf(Set).isRequired,
  onToggleProject: PropTypes.func.isRequired,
  onShowAll: PropTypes.func.isRequired,
};
